// Ambient particle/atmosphere layers reused across screens.
//
// Every layer is `pointer-events: none` and absolutely positioned over the
// background. They are deliberately lightweight — pure CSS animations on a
// handful of DOM nodes, no canvas or rAF loops, so they cost nothing on
// idle screens.

import { useMemo } from 'react';

interface DustProps {
  count?: number;
  tint?: string;
}

// Specks of dust drifting upward — used for noir interiors and lobby idle.
export function Dust({ count = 18, tint = '#f0e6d4' }: DustProps) {
  const items = useMemo(
    () =>
      Array.from({ length: count }).map(() => ({
        left: Math.random() * 100,
        delay: -Math.random() * 8,
        dur: 6 + Math.random() * 8,
        o: 0.3 + Math.random() * 0.5,
      })),
    [count],
  );
  return (
    <div className="dust" style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 1 }}>
      {items.map((p, i) => (
        <i
          key={i}
          style={{
            left: `${p.left}%`,
            background: tint,
            ['--del' as never]: `${p.delay}s`,
            ['--t' as never]: `${p.dur}s`,
            ['--o' as never]: p.o,
          }}
        />
      ))}
    </div>
  );
}

// Two stacked smoke plumes — soft drifting white blur.
export function Smoke() {
  return (
    <>
      <div className="smoke" />
      <div className="smoke s2" />
    </>
  );
}

// Hot embers rising from below — used on home/role-reveal for arsonist
// energy. Keeps to ~12 by default so it stays subtle.
interface EmbersProps {
  count?: number;
  tint?: string;
}
export function Embers({ count = 14, tint = '#ff7a1a' }: EmbersProps) {
  const items = useMemo(
    () =>
      Array.from({ length: count }).map(() => ({
        left: Math.random() * 100,
        delay: -Math.random() * 6,
        dur: 4 + Math.random() * 5,
        size: 2 + Math.random() * 2,
        drift: (Math.random() - 0.5) * 80,
      })),
    [count],
  );
  return (
    <div className="embers">
      {items.map((p, i) => (
        <i
          key={i}
          style={{
            left: `${p.left}%`,
            background: tint,
            width: `${p.size}px`,
            height: `${p.size}px`,
            ['--del' as never]: `${p.delay}s`,
            ['--t' as never]: `${p.dur}s`,
            ['--drift' as never]: `${p.drift}px`,
          }}
        />
      ))}
    </div>
  );
}

// Slow searching spotlight that sweeps from one edge to the other.
export function Spotlight() {
  return <div className="spotlight" />;
}

// Subtle CRT-style scanlines — adds noir/film texture without overwhelming
// content underneath.
export function Scanlines() {
  return <div className="scanlines" />;
}
